import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { isPsHttpError } from '@patsnap/tahiti-core';
import { SharePageService } from '../share-page-service';
import { type ApiPageInitResponse, PageInit } from '@/infrastructure/api-functions/core-drafting-api/page-init-api';
import { PublicShareApi } from '@telecom/shared/infrastructure/api-functions/public-share-api';
import { ShareModel } from '@telecom/shared/domain/permission/models/share-model';
import { CommandService } from '@patsnap/tahiti-core';
import { isUserLogin } from '@telecom/shared/startup/user-auth';
import {
    RavenService,
    type RavenService as RavenServiceType,
} from '@telecom/shared/services/raven-service/raven-service';
import { TRACK_EVENT_TYPE } from '@/constants';
import { TRACK_EVENT_REPORT_SHARE_ACCESS_RESULT } from '@/constants/track-events';

@injectable()
export class SharePageServiceImpl implements SharePageService {
    private _taskId: string = '';
    private _loginPromise: Promise<void> | undefined;

    constructor(
        @inject(PageInit)
        private readonly _pageInit: PageInit,
        @inject(PublicShareApi)
        private readonly _publicShareApi: PublicShareApi,
        @inject(ShareModel)
        private readonly _shareModel: ShareModel,
        @inject(CommandService)
        private readonly _commandService: CommandService,
        @inject(RavenService)
        private readonly _ravenService: RavenServiceType,
    ) {}

    get taskId(): string {
        return this._taskId;
    }

    get shareId(): string {
        return this.getPageQueryValue('shareId') || '';
    }

    get isV2SharePage(): boolean {
        const taskId = this.getPageQueryValue('taskId') || this.getPageQueryValue('task_id') || '';
        return taskId.startsWith('sh_');
    }

    get isSharePage(): boolean {
        const isLegacySharePage = !this._shareModel.modelData.isShareModel && !!this.shareId;
        return isLegacySharePage || this.isV2SharePage;
    }

    async initialize(): Promise<void> {
        if (!this.shareId) {
            return;
        }
        if (this._taskId) {
            return;
        }
        await this._ensureShareViewerLogin();
        try {
            this._taskId = await this._publicShareApi.getShareInit({
                share_id: this.shareId,
            }).then(res => res.task_id);
            this._trackShareAccess('SUCCESS');
        } catch (error) {
            const errorCode = isPsHttpError(error) ? error.cause.error_code : undefined;
            this._trackShareAccess(errorCode === 33110004 ? 'DENIED' : 'FAILED', errorCode);
            throw error;
        }

    }

    // eslint-disable-next-line max-lines-per-function
    public pageInitProxy(): Promise<ApiPageInitResponse> {
        if (this.isSharePage) {
            console.warn('PageInitServiceImpl: getPageInitData: share page, return default data');
            return Promise.resolve({
                permission: {
                    ai_disclosure: true,
                    ai_translation: true,
                    ai_specification_cn: true,
                    ai_specification_us: true,
                    ai_specification_eu: true,
                    ai_novelty_search: true,
                    ai_fto_search: true,
                    ai_image_search: true,
                },
                novelty_search_usage: {
                    usage: 0,
                    total: 0,
                },
                fto_search_usage: {
                    usage: 0,
                    total: 0,
                },
                specification_usage: {
                    usage: 0,
                    total: 0,
                },
                patent_disclosure_usage: {
                    usage: 0,
                    total: 0,
                },
                translation_usage: {
                    usage: 0,
                    total: 0,
                },
                cn_patent_specification_usage: {
                    usage: 0,
                    total: 0,
                },
                us_patent_specification_usage: {
                    usage: 0,
                    total: 0,
                },
                eu_patent_specification_usage: {
                    usage: 0,
                    total: 0,
                },
                unified_patent_specification_usage: {
                    usage: 0,
                    total: 0,
                },
                unified_usage: {
                    usage: 0,
                    total: 0,
                },
                unified_pro_usage: {
                    usage: 0,
                    total: 0,
                },
                unified_free_usage: {
                    usage: 0,
                    total: 0,
                },
                time_zone: {
                    utc_offset: 0,
                    value: 'UTC',
                },
                tier_code: 'FREE',
                workspace_permission: false,
            });
        }
        return this._pageInit.pageInit();
    }

    private getPageQueryValue(key: string): string | null {
        const url = new URL(window.location.href);
        const value = url.searchParams.get(key);
        if (value) {
            return value;
        }
        if (!window.location.hash) {
            return null;
        }
        const hashQueryStart = window.location.hash.indexOf('?');
        if (hashQueryStart < 0) {
            return null;
        }
        const hashQuery = window.location.hash.slice(hashQueryStart + 1);
        return new URLSearchParams(hashQuery).get(key);
    }

    private async _ensureShareViewerLogin(): Promise<void> {
        if (await isUserLogin()) {
            return;
        }
        this._loginPromise ??= this._openRequiredLoginDialog();
        await this._loginPromise;
    }

    private async _openRequiredLoginDialog(): Promise<void> {
        // 当前调用通常发生在页面 setup 阶段，让挂载点先完成渲染。
        await Promise.resolve();
        const result = await this._commandService.executeCommand('login:open-dialog', {
            trigger_source: 'report_share_access',
            withoutClose: true,
        });
        if (result.action !== 'submit') {
            this._loginPromise = undefined;
            throw new Error('Share report login is required');
        }
    }

    private _trackShareAccess(result: 'SUCCESS' | 'DENIED' | 'FAILED', errorCode?: number): void {
        const currentUrl = `${window.location.pathname}${window.location.hash}`;
        const agentType = currentUrl.includes('novelty')
            ? 'novelty'
            : currentUrl.includes('fto') ? 'fto' : 'unknown';
        void this._ravenService.track(TRACK_EVENT_REPORT_SHARE_ACCESS_RESULT, {
            eventType: TRACK_EVENT_TYPE.Drafting,
            eventDetailStr: {
                agent_type: agentType,
                result,
                ...(errorCode === undefined ? {} : { error_code: errorCode }),
            },
        }).catch(() => undefined);
    }
}
